import { observable, observe } from '@legendapp/state';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getUserProfile, upsertUserProfile } from './userHelpers';

export type SubscriptionTier = 'free' | 'premium' | 'vip';

export interface RelationshipData {
  level: number;
  affectionScore: number;
}

interface PersistedState {
  coins: number;
  streak: number;
  streakBest: number;
  streakLastDate: string | null;
  spicyEnabled: boolean;
  language: string;
  unlockedCharacterIds: string[];
  userName: string | null;
  subscriptionTier: SubscriptionTier;
  subscriptionExpiresAt: string | null;
  dailyMessageCount: number;
  dailyMessageResetAt: string | null;
  relationshipLevels: Record<string, RelationshipData>;
  customCharacterIds: string[];
}

const STORAGE_KEY = '@virtualhalf:appState';

const defaultState: PersistedState = {
  coins: 10,
  streak: 1,
  streakBest: 1,
  streakLastDate: null,
  spicyEnabled: false,
  language: 'en',
  unlockedCharacterIds: [],
  userName: null,
  subscriptionTier: 'free',
  subscriptionExpiresAt: null,
  dailyMessageCount: 0,
  dailyMessageResetAt: null,
  relationshipLevels: {},
  customCharacterIds: [],
};

export const appState$ = observable<PersistedState>({ ...defaultState });

let loaded = false;
let syncTimeout: NodeJS.Timeout | null = null;

function persist(state: PersistedState) {
  AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state)).catch((e) =>
    console.error('[AppState] Persist error:', e)
  );
}

function scheduleSync() {
  if (syncTimeout) clearTimeout(syncTimeout);
  syncTimeout = setTimeout(() => {
    syncToSupabase();
    syncTimeout = null;
  }, 1500);
}

async function syncToSupabase() {
  const state = appState$.get();
  await upsertUserProfile({
    coins: state.coins,
    streak: state.streak,
    streak_best: state.streakBest,
    streak_last_date: state.streakLastDate,
    spicy_enabled: state.spicyEnabled,
    language: state.language,
    unlocked_character_ids: state.unlockedCharacterIds,
    user_name: state.userName,
    subscription_tier: state.subscriptionTier,
    subscription_expires_at: state.subscriptionExpiresAt,
    daily_message_count: state.dailyMessageCount,
    daily_message_reset_at: state.dailyMessageResetAt,
    vip_badge: state.subscriptionTier === 'vip',
  });
}

observe(appState$, () => {
  const state = appState$.get();
  persist(state);
  if (loaded) {
    scheduleSync();
  }
});

export async function loadAppState(): Promise<void> {
  if (loaded) return;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      appState$.set({
        ...defaultState,
        ...parsed,
      });
    }
    loaded = true;
    await loadFromSupabase();
    checkStreak();
  } catch (e) {
    console.error('[AppState] Load error:', e);
  }
}

async function loadFromSupabase(): Promise<void> {
  const profile = await getUserProfile();
  if (!profile) return;

  appState$.set({
    coins: profile.coins ?? defaultState.coins,
    streak: profile.streak ?? defaultState.streak,
    streakBest: profile.streak_best ?? defaultState.streakBest,
    streakLastDate: profile.streak_last_date ?? defaultState.streakLastDate,
    spicyEnabled: profile.spicy_enabled ?? defaultState.spicyEnabled,
    language: profile.language ?? defaultState.language,
    unlockedCharacterIds: profile.unlocked_character_ids || [],
    userName: profile.user_name ?? defaultState.userName,
    subscriptionTier: (profile.subscription_tier as SubscriptionTier) ?? defaultState.subscriptionTier,
    subscriptionExpiresAt: profile.subscription_expires_at ?? defaultState.subscriptionExpiresAt,
    dailyMessageCount: profile.daily_message_count ?? defaultState.dailyMessageCount,
    dailyMessageResetAt: profile.daily_message_reset_at ?? defaultState.dailyMessageResetAt,
    relationshipLevels: appState$.relationshipLevels.get(),
    customCharacterIds: appState$.customCharacterIds.get(),
  });
}

export async function loadUserProfile(): Promise<void> {
  if (!loaded) {
    console.warn('[AppState] loadAppState must be called before loadUserProfile');
    return;
  }
  await loadFromSupabase();
}

function localDateString(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function checkStreak(): void {
  const state = appState$.get();
  const today = localDateString();
  if (state.streakLastDate === today) return;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yestStr = localDateString(yesterday);

  if (state.streakLastDate === yestStr) {
    const newStreak = state.streak + 1;
    appState$.streak.set(newStreak);
    if (newStreak > state.streakBest) {
      appState$.streakBest.set(newStreak);
    }
  } else {
    appState$.streak.set(1);
    if (1 > state.streakBest) {
      appState$.streakBest.set(1);
    }
  }
  appState$.streakLastDate.set(today);
  addCoins(5);
}

export function addCoins(amount: number): void {
  appState$.coins.set((c) => c + amount);
}

export function spendCoins(amount: number): boolean {
  const current = appState$.coins.get();
  if (current < amount) return false;
  appState$.coins.set(current - amount);
  return true;
}

export function isCharacterUnlocked(characterId: string): boolean {
  const unlocked = appState$.unlockedCharacterIds.get();
  return unlocked.includes(characterId);
}

export function unlockCharacter(characterId: string): void {
  appState$.unlockedCharacterIds.set((ids) => {
    if (ids.includes(characterId)) return ids;
    return [...ids, characterId];
  });
}

export function setSpicyEnabled(enabled: boolean): void {
  appState$.spicyEnabled.set(enabled);
}

export function setLanguage(lang: string): void {
  appState$.language.set(lang);
}

export function setUserName(name: string) {
  appState$.userName.set(name);
}

// --- Subscription helpers ---

export function isSubscribed(): boolean {
  const tier = appState$.subscriptionTier.get();
  if (tier === 'free') return false;
  const expiresAt = appState$.subscriptionExpiresAt.get();
  if (!expiresAt) return true;
  return new Date(expiresAt) > new Date();
}

export function isVip(): boolean {
  const tier = appState$.subscriptionTier.get();
  if (tier !== 'vip') return false;
  const expiresAt = appState$.subscriptionExpiresAt.get();
  if (!expiresAt) return true;
  return new Date(expiresAt) > new Date();
}

export function getSubscriptionTier(): SubscriptionTier {
  if (isVip()) return 'vip';
  if (isSubscribed()) return appState$.subscriptionTier.get();
  return 'free';
}

export function setSubscriptionTier(tier: SubscriptionTier, expiresAt?: string | null) {
  appState$.subscriptionTier.set(tier);
  appState$.subscriptionExpiresAt.set(expiresAt ?? null);
}

// --- Message quota helpers ---

export const FREE_DAILY_MESSAGE_LIMIT = 20;
export const PREMIUM_DAILY_MESSAGE_LIMIT = 200;
export const VIP_DAILY_MESSAGE_LIMIT = 9999;

function resetDailyMessageCountIfNeeded() {
  const today = localDateString();
  const resetAt = appState$.dailyMessageResetAt.get();
  if (resetAt !== today) {
    appState$.dailyMessageCount.set(0);
    appState$.dailyMessageResetAt.set(today);
  }
}

export function getDailyMessageLimit(): number {
  if (isVip()) return VIP_DAILY_MESSAGE_LIMIT;
  if (isSubscribed()) return PREMIUM_DAILY_MESSAGE_LIMIT;
  return FREE_DAILY_MESSAGE_LIMIT;
}

export function getRemainingMessages(): number {
  resetDailyMessageCountIfNeeded();
  const limit = getDailyMessageLimit();
  const used = appState$.dailyMessageCount.get();
  return Math.max(0, limit - used);
}

export function canSendMessage(): boolean {
  resetDailyMessageCountIfNeeded();
  const limit = getDailyMessageLimit();
  return appState$.dailyMessageCount.get() < limit;
}

export function incrementMessageCount(): void {
  resetDailyMessageCountIfNeeded();
  appState$.dailyMessageCount.set((c) => c + 1);
}

// --- Relationship helpers ---

const AFFECTION_PER_LEVEL = 100;

export function getRelationshipLevel(characterId: string): RelationshipData {
  const levels = appState$.relationshipLevels.get();
  return levels[characterId] ?? { level: 1, affectionScore: 0 };
}

export function addAffection(characterId: string, amount: number): void {
  appState$.relationshipLevels.set((levels) => {
    const current = levels[characterId] ?? { level: 1, affectionScore: 0 };
    let newAffection = current.affectionScore + amount;
    let newLevel = current.level;
    while (newAffection >= AFFECTION_PER_LEVEL) {
      newAffection -= AFFECTION_PER_LEVEL;
      newLevel += 1;
    }
    return {
      ...levels,
      [characterId]: { level: newLevel, affectionScore: newAffection },
    };
  });
}

// --- Custom character helpers ---

export function addCustomCharacterId(characterId: string): void {
  appState$.customCharacterIds.set((ids) => {
    if (ids.includes(characterId)) return ids;
    return [...ids, characterId];
  });
}

export function isCustomCharacter(characterId: string): boolean {
  return appState$.customCharacterIds.get().includes(characterId);
}

const adjectives = [
  'Brave', 'Cool', 'Sunny', 'Lucky', 'Happy', 'Fierce', 'Wild', 'Silent',
  'Swift', 'Bold', 'Mystic', 'Dreamy', 'Golden', 'Royal', 'Cosmic', 'Neon',
];

const nouns = [
  'Lion', 'Tiger', 'Wolf', 'Bear', 'Eagle', 'Fox', 'Dragon', 'Phoenix',
  'Knight', 'Rider', 'Ghost', 'Shadow', 'Star', 'Moon', 'Storm', 'Wave',
];

export function generateRandomUsername(): string {
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(1000 + Math.random() * 9000);
  return `${adj}${noun}${num}`;
}
